import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { DeploymentBySource } from '../types/api';
import { getApiErrorMessage } from '../constants/messages';
import { Modal, Button } from './ui';
import Logger from '../utils/logger';
import { useTranslation } from 'react-i18next';

interface DeployBuildModalProps {
  isOpen: boolean;
  onClose: () => void;
  buildId: string;
  /** La source du build. Sans elle on ne sait pas quelles cibles proposer : le bouton
   *  ne doit alors pas etre offert du tout, plutot que d'ouvrir une liste vide. */
  connectedRepositoryId: number;
  branch: string | null;
  /** Ce que le build a produit, pour que la personne voie ce qu'elle met en service.
   *  Affichage seulement : le SERVEUR resout le depot et le tag depuis le build --
   *  la console n'en compose aucun, c'est ce qui a casse circuit-workflow. */
  imageUrl?: string | null;
  imageTag?: string | null;
  onDeployed?: () => void;
}

const DeployBuildModal: React.FC<DeployBuildModalProps> = ({
  isOpen,
  onClose,
  buildId,
  connectedRepositoryId,
  branch,
  imageUrl,
  imageTag,
  onDeployed,
}) => {
  const { t } = (useTranslation as any)('builds');
  const [cibles, setCibles] = useState<DeploymentBySource[] | null>(null);
  const [choisie, setChoisie] = useState<string>('');
  const [chargement, setChargement] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await apiService.getDeploymentsBySource(
        connectedRepositoryId, branch || undefined);
      setCibles(r.deploiements);
      // Pre-selection : un deploiement qui suit DEJA ce depot est le candidat evident.
      const suit = r.deploiements.find((d) => d.suit_ce_depot);
      setChoisie(suit ? suit.deployment_id : '');
    } catch (error) {
      Logger.error('Chargement des cibles de deploiement:', error);
      // `null` distingue « on ne sait pas » de « aucune cible » -- l'ecran ne doit pas
      // annoncer qu'il n'y a aucun deploiement quand c'est la lecture qui a echoue.
      setCibles(null);
      toast.error(getApiErrorMessage(error));
    } finally {
      setChargement(false);
    }
  }, [connectedRepositoryId, branch]);

  useEffect(() => {
    if (isOpen) {
      charger();
    } else {
      setCibles(null);
      setChoisie('');
    }
  }, [isOpen, charger]);

  const deployer = async () => {
    if (!choisie) return;
    setEnvoi(true);
    try {
      const r = await apiService.deployBuild(buildId, choisie);
      if (r.statut === 'inchange') {
        toast.success(t('deploy.unchanged') as string);
      } else {
        // Le changement de depot est l'information qui manquait au Go Live : une image
        // peut venir d'un AUTRE depot que celui que portait le deploiement.
        toast.success(r.changement_de_depot
          ? (t('deploy.doneRepoChanged') as string)
          : (t('deploy.done') as string));
      }
      onDeployed?.();
      onClose();
    } catch (error) {
      Logger.error('Mise en service du build:', error);
      // Coupe en vol (aucune reponse, ou 502/504 de la passerelle) : la propagation est
      // SYNCHRONE et attend le rollout, donc elle peut depasser les 120 s de l'ingress
      // alors qu'elle a abouti. Mesure du 2026-10-11 : 504 rendu, et l'image du
      // deploiement avait bien change. Annoncer un echec invite a recliquer -- et
      // c'est le « 200 OK mais rien deploye » a l'envers, le sens le plus couteux.
      const statut = (error as { response?: { status?: number } })?.response?.status;
      if (statut === undefined || statut === 502 || statut === 504) {
        toast.success(t('deploy.maybeApplied') as string);
        onDeployed?.();
        onClose();
      } else {
        toast.error(getApiErrorMessage(error));
      }
    } finally {
      setEnvoi(false);
    }
  };

  const aucuneCible = cibles !== null && cibles.length === 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('deploy.title') as string}>
      <div className="space-y-4">
        <div className="bg-surface-3 rounded-lg p-3 text-sm">
          <div className="text-text-tertiary">{t('deploy.whatShips') as string}</div>
          <div className="text-text-primary font-mono text-xs mt-1 break-all">
            {imageUrl || buildId}{imageTag && !String(imageUrl || '').includes(`:${imageTag}`)
              ? `:${imageTag}` : ''}
          </div>
          {branch && (
            <div className="text-text-tertiary text-xs mt-2">
              {t('deploy.fromBranch', { branch }) as string}
            </div>
          )}
        </div>

        {chargement && (
          <p className="text-sm text-text-tertiary">{t('deploy.loading') as string}</p>
        )}

        {!chargement && aucuneCible && (
          <div className="rounded-lg border border-border-1 bg-surface-3/50 p-3">
            <p className="text-sm text-text-primary">{t('deploy.noTarget') as string}</p>
            <p className="text-xs text-text-tertiary mt-1">
              {t('deploy.noTargetHint') as string}
            </p>
          </div>
        )}

        {!chargement && cibles && cibles.length > 0 && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-text-secondary">
              {t('deploy.chooseTarget') as string}
            </label>
            {cibles.map((d) => (
              <label
                key={d.deployment_id}
                className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                  choisie === d.deployment_id
                    ? 'border-accent-400 bg-accent-500/10'
                    : 'border-border-1 hover:bg-surface-3'
                }`}
              >
                <input
                  type="radio"
                  name="cible-deploiement"
                  className="mt-1"
                  checked={choisie === d.deployment_id}
                  onChange={() => setChoisie(d.deployment_id)}
                />
                <span className="min-w-0">
                  <span className="block text-sm text-text-primary">{d.nom}</span>
                  <span className="block text-xs text-text-tertiary mt-0.5">
                    {d.suit_ce_depot
                      ? (t('deploy.followsThisRepo') as string)
                      : (t('deploy.followsOther') as string)}
                    {d.auto_deploy_enabled && ` · ${t('deploy.autoOn') as string}`}
                    {d.git_branch && ` · ${d.git_branch}`}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose} disabled={envoi}>
            {t('cancel', { ns: 'common' }) as string}
          </Button>
          <Button onClick={deployer} disabled={!choisie || envoi || chargement}>
            {envoi ? (t('deploy.sending') as string) : (t('deploy.confirm') as string)}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DeployBuildModal;
